import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Chart as ChartJS,
  LinearScale,
  TimeScale,
  Tooltip,
} from "chart.js";
import "chartjs-adapter-date-fns";
import {
  CandlestickController,
  CandlestickElement,
} from "chartjs-chart-financial";

ChartJS.register(
  CandlestickController,
  CandlestickElement,
  LinearScale,
  TimeScale,
  Tooltip,
);

const RANGE_CONFIG = {
  "1D": { candles: 24, stepMs: 15 * 60 * 1000, updateMs: 1800, drift: 0.0012, volatility: 0.007 },
  "1W": { candles: 20, stepMs: 24 * 60 * 60 * 1000, updateMs: 2200, drift: 0.0022, volatility: 0.014 },
  "1M": { candles: 22, stepMs: 24 * 60 * 60 * 1000, updateMs: 2600, drift: 0.0032, volatility: 0.022 },
};

const seededNoise = (seed, index) => {
  const value = Math.sin(seed * 12.9898 + index * 78.233) * 43758.5453;
  return value - Math.floor(value);
};

const buildCandles = ({ basePrice, trendBias, seed, range }) => {
  const config = RANGE_CONFIG[range] || RANGE_CONFIG["1D"];
  const now = Date.now();
  const start = now - config.stepMs * (config.candles - 1);
  let previousClose = Math.max(1, basePrice * (1 - config.volatility * 1.8));

  return Array.from({ length: config.candles }, (_, index) => {
    const noise = seededNoise(seed, index) - 0.5;
    const driftWave = Math.sin((index / config.candles) * Math.PI * 2.1 + seed) * config.volatility * 0.55;
    const directionalMove = trendBias * config.drift + driftWave + noise * config.volatility;
    const open = previousClose;
    const close = Math.max(1, open * (1 + directionalMove));
    const wickSpread = Math.max(open, close) * (0.002 + Math.abs(noise) * 0.006);
    const high = Math.max(open, close) + wickSpread;
    const low = Math.max(0.5, Math.min(open, close) - wickSpread * 0.9);

    previousClose = close;

    return {
      x: start + index * config.stepMs,
      o: Number(open.toFixed(2)),
      h: Number(high.toFixed(2)),
      l: Number(low.toFixed(2)),
      c: Number(close.toFixed(2)),
    };
  });
};

const LiveCandlestickChart = ({
  basePrice = 100,
  trendBias = 1,
  seed = 1,
  range = "1D",
}) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const config = RANGE_CONFIG[range] || RANGE_CONFIG["1D"];
  const initialCandles = useMemo(
    () => buildCandles({ basePrice, trendBias, seed, range }),
    [basePrice, trendBias, seed, range],
  );
  const [candles, setCandles] = useState(initialCandles);

  useEffect(() => {
    setCandles(initialCandles);
  }, [initialCandles]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCandles((prev) => {
        if (!prev.length) return prev;

        const next = [...prev];
        const last = next[next.length - 1];
        const tickSeed = seed + Date.now() / config.updateMs;
        const noise = seededNoise(tickSeed, next.length) - 0.5;
        const drift = trendBias * config.drift * 0.3 + noise * config.volatility * 0.45;
        const close = Math.max(1, last.c * (1 + drift));
        const high = Math.max(last.h, close + Math.abs(noise) * last.c * 0.004);
        const low = Math.max(0.5, Math.min(last.l, close - Math.abs(noise) * last.c * 0.004));

        next[next.length - 1] = {
          ...last,
          h: Number(high.toFixed(2)),
          l: Number(low.toFixed(2)),
          c: Number(close.toFixed(2)),
        };

        const shouldAppend =
          Date.now() - last.x >= config.updateMs * 2.2;

        if (shouldAppend) {
          const open = next[next.length - 1].c;
          const nextNoise = seededNoise(tickSeed, next.length + 1) - 0.5;
          const nextClose = Math.max(1, open * (1 + trendBias * config.drift * 0.25 + nextNoise * config.volatility));
          const wickSpread = Math.max(open, nextClose) * (0.002 + Math.abs(nextNoise) * 0.005);

          next.push({
            x: last.x + config.stepMs,
            o: Number(open.toFixed(2)),
            h: Number((Math.max(open, nextClose) + wickSpread).toFixed(2)),
            l: Number((Math.min(open, nextClose) - wickSpread * 0.85).toFixed(2)),
            c: Number(nextClose.toFixed(2)),
          });

          if (next.length > config.candles) {
            next.shift();
          }
        }

        return next;
      });
    }, config.updateMs);

    return () => window.clearInterval(intervalId);
  }, [config.candles, config.drift, config.stepMs, config.updateMs, config.volatility, seed, trendBias]);

  useEffect(() => {
    if (!canvasRef.current || chartRef.current) return undefined;

    chartRef.current = new ChartJS(canvasRef.current, {
      type: "candlestick",
      data: {
        datasets: [
          {
            label: "Price",
            data: candles,
            borderColor: {
              up: "#16A34A",
              down: "#DC2626",
              unchanged: "#64748B",
            },
            color: {
              up: "#22C55E",
              down: "#EF4444",
              unchanged: "#94A3B8",
            },
            barPercentage: 0.72,
            categoryPercentage: 0.84,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: "index",
          intersect: false,
        },
        animation: {
          duration: 420,
          easing: "easeOutQuart",
        },
        parsing: false,
        layout: {
          padding: {
            top: 8,
            right: 6,
            bottom: 2,
            left: 4,
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            backgroundColor: "rgba(255,255,255,0.98)",
            titleColor: "#2457BF",
            bodyColor: "#334155",
            borderColor: "#E2E8F0",
            borderWidth: 1,
            padding: 14,
            displayColors: false,
            cornerRadius: 14,
            titleSpacing: 6,
            bodySpacing: 5,
            caretPadding: 10,
            titleFont: {
              size: 11,
              weight: "700",
            },
            bodyFont: {
              size: 11,
              weight: "600",
            },
            callbacks: {
              title(items) {
                const point = items?.[0]?.raw || {};
                return range === "1D"
                  ? new Date(point.x || Date.now()).toLocaleTimeString("en-IN", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : new Date(point.x || Date.now()).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    });
              },
              label(context) {
                const point = context.raw || {};
                return [
                  `Open  : ₹${Number(point.o || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                  `High  : ₹${Number(point.h || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                  `Low   : ₹${Number(point.l || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                  `Close : ₹${Number(point.c || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                ];
              },
              labelTextColor() {
                return "#334155";
              },
            },
          },
        },
        scales: {
          x: {
            type: "time",
            time: {
              unit: range === "1D" ? "hour" : "day",
              displayFormats: range === "1D" ? { hour: "HH:mm" } : { day: "d MMM" },
            },
            grid: {
              color: "rgba(226, 232, 240, 0.55)",
              drawTicks: false,
            },
            ticks: {
              color: "#64748B",
              maxRotation: 0,
              padding: 8,
              font: {
                size: 11,
                weight: "500",
              },
            },
            border: {
              display: false,
            },
          },
          y: {
            grid: {
              color: "rgba(226, 232, 240, 0.55)",
              drawTicks: false,
            },
            ticks: {
              color: "#64748B",
              padding: 10,
              font: {
                size: 11,
                weight: "500",
              },
              callback(val) {
                return `₹${Number(val).toLocaleString("en-IN", {
                  maximumFractionDigits: 0,
                })}`;
              },
            },
            border: {
              display: false,
            },
          },
        },
      },
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [candles, range]);

  useEffect(() => {
    if (!chartRef.current) return;

    chartRef.current.data.datasets[0].data = candles;
    chartRef.current.options.scales.x.time = {
      unit: range === "1D" ? "hour" : "day",
      displayFormats: range === "1D" ? { hour: "HH:mm" } : { day: "d MMM" },
    };
    chartRef.current.update();
  }, [candles, range]);

  return <canvas ref={canvasRef} />;
};

export default LiveCandlestickChart;
